import { fetcher } from "@/lib/fetcher";
import { ApiResponse } from "./model";
import { OnboardingProgress } from "@/types/onboarding";
import { mutate } from "swr";
import { getActiveWorkspaceScopeKey } from "@/lib/active-workspace";

export const ONBOARDING_PROGRESS_KEY = "/api/ai-transform/v1/onboarding/progress";

export function getOnboardingProgressCacheKey(workspaceScope: string) {
  return [ONBOARDING_PROGRESS_KEY, workspaceScope] as const;
}

export function isOnboardingProgressCacheKey(key: unknown): boolean {
  return Array.isArray(key) && key[0] === ONBOARDING_PROGRESS_KEY;
}

/**
 * Fetch onboarding progress for the current user
 */
export async function fetchOnboardingProgress(): Promise<OnboardingProgress | null> {
  try {
    const response = await fetcher<ApiResponse<OnboardingProgress[]>>(
      ONBOARDING_PROGRESS_KEY,
    );

    if (response.status === "OK" && response.data?.[0]) {
      return response.data[0];
    }

    return null;
  } catch (error) {
    console.error("Failed to fetch onboarding progress:", error);
    return null;
  }
}

export async function prefetchOnboardingProgress(): Promise<
  ApiResponse<OnboardingProgress[]>
> {
  try {
    const response = await fetcher<ApiResponse<OnboardingProgress[]>>(
      ONBOARDING_PROGRESS_KEY,
    );
    return response;
  } catch (error) {
    console.error("Failed to prefetch onboarding progress:", error);
    return {
      status: "OK",
      data: [],
    };
  }
}

/**
 * Best-effort progress refresh after onboarding mutations complete.
 * Uses the same backend endpoint as Home guidance.
 */
export async function revalidateOnboardingProgress(
  workspaceScope = getActiveWorkspaceScopeKey(),
): Promise<OnboardingProgress | null> {
  const response = await prefetchOnboardingProgress();
  const nextProgress =
    response.status === "OK" && response.data?.[0] ? response.data[0] : null;

  if (workspaceScope !== "pending") {
    await mutate(getOnboardingProgressCacheKey(workspaceScope), response, {
      revalidate: false,
    });
  }

  return nextProgress;
}

/**
 * Update collapse state of the onboarding guide
 */
export async function updateCollapseState(
  isCollapsed: boolean,
): Promise<OnboardingProgress | null> {
  try {
    const response = await fetcher<ApiResponse<OnboardingProgress[]>>(
      "/api/ai-transform/v1/onboarding/collapse",
      {
        method: "PUT",
        body: JSON.stringify({ is_collapsed: isCollapsed }),
      },
    );

    if (response.status === "OK" && response.data?.[0]) {
      return response.data[0];
    }

    return null;
  } catch (error) {
    console.error("Failed to update collapse state:", error);
    return null;
  }
}
